import axios from "axios";

// Use environment variable for production
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8080/api";

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000, // 10 second timeout
});

// Sanitize user input to prevent XSS
const sanitizeInput = (input) => {
  if (typeof input === "string") {
    return input
      .replace(/[<>]/g, "") // Remove < and >
      .trim()
      .substring(0, 1000); // Max length 1000 chars
  }
  return input;
};

// Request interceptor - Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor - Handle 401 errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only auto-logout on 401 if it's NOT from an admin action
    if (error.response?.status === 401) {
      const url = error.config?.url || "";

      // Don't auto-logout for admin operations (they might just be permission errors)
      if (
        !url.includes("/admin") &&
        !url.includes("/items") &&
        !url.includes("/lists") &&
        !url.includes("/categories")
      ) {
        localStorage.removeItem("token");
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  },
);

// Auth API
export const authAPI = {
  login: (credentials) =>
    api.post("/auth/login", {
      usernameOrEmail: sanitizeInput(credentials.usernameOrEmail),
      password: credentials.password,
    }),
  register: (userData) =>
    api.post("/auth/register", {
      username: sanitizeInput(userData.username),
      email: sanitizeInput(userData.email),
      password: userData.password, // Don't sanitize password
    }),
};

// User API
export const userAPI = {
  getCurrentUser: () => api.get("/user/me"),
  updateUser: (userData) =>
    api.put("/user/update", {
      username: userData.username
        ? sanitizeInput(userData.username)
        : undefined,
      email: userData.email ? sanitizeInput(userData.email) : undefined,
    }),
  changePassword: (passwordData) =>
    api.put("/user/change-password", passwordData),
  deleteAccount: () => api.delete("/user/delete"),
};

// Category API
export const categoryAPI = {
  getAllCategories: () => api.get("/categories/public"),
  getCategoryById: (id) => api.get(`/categories/public/${id}`),
  getCategoryBySlug: (slug) => api.get(`/categories/public/slug/${slug}`),
  createCategory: (categoryData) =>
    api.post("/categories", {
      name: sanitizeInput(categoryData.name),
      description: sanitizeInput(categoryData.description),
    }),
  updateCategory: (id, categoryData) =>
    api.put(`/categories/${id}`, {
      name: sanitizeInput(categoryData.name),
      description: sanitizeInput(categoryData.description),
    }),
  deleteCategory: (id) => api.delete(`/categories/${id}`),
};

// List API
export const listAPI = {
  getPublicLists: (page = 0, size = 20, sortBy = "recent") =>
    api.get(`/lists/public?page=${page}&size=${size}&sortBy=${sortBy}`),
  getListsByCategory: (categoryId, page = 0, size = 20, sortBy = "recent") =>
    api.get(
      `/lists/public/category/${categoryId}?page=${page}&size=${size}&sortBy=${sortBy}`,
    ),
  searchLists: (query, page = 0, size = 20) =>
    api.get(
      `/lists/search?query=${encodeURIComponent(
        sanitizeInput(query),
      )}&page=${page}&size=${size}`,
    ),
  getListDetail: (id) => api.get(`/lists/public/${id}`),
  getListDetailBySlug: (slug) => api.get(`/lists/public/slug/${slug}`),
  createList: (listData) =>
    api.post("/lists", {
      ...listData,
      title: sanitizeInput(listData.title),
      description: sanitizeInput(listData.description),
    }),
  updateList: (id, listData) =>
    api.put(`/lists/${id}`, {
      ...listData,
      title: sanitizeInput(listData.title),
      description: sanitizeInput(listData.description),
    }),
  deleteList: (id) => api.delete(`/lists/${id}`),
  getAllListsAdmin: (page = 0, size = 20) =>
    api.get(`/lists/admin/all?page=${page}&size=${size}`),
  // NEW: Efficient admin search endpoint
  searchListsAdmin: (query, page = 0, size = 100) =>
    api.get(
      `/lists/admin/search?query=${encodeURIComponent(
        sanitizeInput(query),
      )}&page=${page}&size=${size}`,
    ),
};

// Item API
export const itemAPI = {
  getItemsByListId: (listId) => api.get(`/items/list/${listId}`),
  addItemToList: (listId, itemData) =>
    api.post(`/items/list/${listId}`, {
      answer: sanitizeInput(itemData.answer),
      rankPosition: itemData.rankPosition,
    }),
  updateItem: (itemId, itemData) =>
    api.put(`/items/${itemId}`, {
      answer: sanitizeInput(itemData.answer),
      rankPosition: itemData.rankPosition,
    }),
  deleteItem: (itemId) => api.delete(`/items/${itemId}`),
  deleteAllItems: (listId) => api.delete(`/items/list/${listId}/all`),
};

// Guess API
export const guessAPI = {
  submitGuess: async (
    listId,
    guess,
    difficultyMode,
    isTimedMode,
    elapsedSeconds,
  ) => {
    return api.post(`/guess/${listId}`, {
      guess,
      difficultyMode,
      isTimedMode,
      elapsedSeconds,
    });
  },

  resetProgress: async (listId) => {
    return api.delete(`/guess/${listId}/reset`);
  },

  // Update timer without submitting a guess
  updateTimer: async (listId, elapsedSeconds) => {
    return api.put(`/guess/${listId}/timer`, {
      elapsedSeconds,
    });
  },
};

// Progress API
export const progressAPI = {
  getUserProgress: () => api.get("/progress"),
  getInProgressLists: () => api.get("/progress/in-progress"),
  getCompletedLists: () => api.get("/progress/completed"),
};

// Analytics API
export const analyticsAPI = {
  getOverallAnalytics: () => api.get("/admin/analytics/overview"),
  getListAnalytics: (listId) => api.get(`/admin/analytics/list/${listId}`),
  getAllListsAnalytics: () => api.get("/admin/analytics/lists/all"),
  getAllUsers: () => api.get("/admin/users"),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
};

export default api;
